import axios, { AxiosError, AxiosProgressEvent, AxiosRequestConfig, CancelTokenSource } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { AsyncResult, IAsyncTask } from '../core';
import { Store } from '../store/store';
import { StoreNode } from '../store/storeNode';

export type ApiMultipartUploadRequestParams = {
  url: string,
  blob: Blob;
  part: number;
  fileSize: number;
  contentType: string;
  stallTimeoutMs?: number;
}

/**
 * A part that sends no bytes for this long is on a dead connection. The browser never times an
 * upload out on its own, so without this the part -- and the upload -- sat stuck forever (BUG-022).
 */
const STALL_TIMEOUT_MS = 60_000;

export class ApiMultipartUploadRequest
  extends StoreNode
  implements IAsyncTask {

  constructor(params: ApiMultipartUploadRequestParams, store: Store) {
    super(store);
    makeObservable<this, 'handleUploadProgress'>(this, {
      promise: observable,
      error: observable,
      isActive: observable,
      uploadTotal: observable,
      uploadLoaded: observable,
      uploadProgress: computed,
      handleUploadProgress: action,
    });

    this.url = params.url;
    this.blob = params.blob;
    this.part = params.part;
    this.fileSize = params.fileSize;
    this.contentType = params.contentType;
    this.stallTimeoutMs = params.stallTimeoutMs ?? STALL_TIMEOUT_MS;
  }

  readonly url: string;
  readonly blob: Blob;
  readonly part: number;
  readonly fileSize: number;
  readonly contentType: string;
  readonly stallTimeoutMs: number;

  promise: AsyncResult | null = null;
  error: unknown = null;
  isActive: boolean = false;
  uploadTotal: number = 0;
  uploadLoaded: number = 0;
  cancelToken?: CancelTokenSource;

  get uploadProgress(): number {
    return this.uploadLoaded / this.uploadTotal;
  }

  cancel() {
    this.cancelToken?.cancel();
  }

  private async startRequest(): AsyncResult {
    this.isActive = true;

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    this.cancelToken = source;

    const { blob, url } = this;

    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const armStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        source.cancel();
      }, this.stallTimeoutMs);
    };

    const config: AxiosRequestConfig = {
      cancelToken: source.token,
      onUploadProgress: (evt) => {
        armStallTimer();
        this.handleUploadProgress(evt);
      },
    };

    armStallTimer();
    try {
      const resData = await axios.put(url, blob, config);
      this.isActive = false;

      return [resData];
    } catch (err) {
      this.error = err;
      this.isActive = false;
      // Reported as a dropped connection (no response), so the part retry ladder re-sends it.
      if (stalled)
        return [null, new AxiosError('Upload stalled', AxiosError.ECONNABORTED)];
      if (axios.isCancel(err)) {
        return [null, new Error('Cancel')];
      }
      return [null, err];
    } finally {
      clearTimeout(stallTimer);
    }
  }

  async start(): AsyncResult {
    // wrapper around startRequest which intercepts the promise
    const promise = this.startRequest();
    this.promise = promise;
    return promise;
  }

  private handleUploadProgress = (evt: AxiosProgressEvent) => {
    if (evt.total) {
      this.uploadLoaded = evt.loaded;
      this.uploadTotal = evt.total;
    }
  }
}